import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useTheme } from '@material-ui/core/styles';
import MemoryIcon from '@material-ui/icons/Memory';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';
import { OciApproverPickerComponent } from './OciApproverPicker';

export type OciShapeSummary = {
  name: string;
  ocpus?: number;
  memoryInGBs?: number;
  processorDescription?: string;
  ocpuMin?: number;
  ocpuMax?: number;
  memoryMinGb?: number;
  memoryMaxGb?: number;
};

export type OciShapeConfig = {
  shape: string;
  processorDescription?: string;
  ocpuMin: number;
  ocpuMax: number;
  memoryMinGb: number;
  memoryMaxGb: number;
  ocpus: number;
  memoryInGBs: number;
  // Present only when ocpus/memoryInGBs exceed the standard caps below —
  // an admin has to sign off on the increase.
  assignedApprover?: string;
  overageReason?: string;
};

type Series = 'AMD' | 'Intel' | 'Other';

// Standard self-service caps. Anything above this needs an approver + a
// reason, captured via the "Want more?" dialog below.
export const MAX_OCPUS = 16;
export const MAX_MEMORY_GB = 64;

function classifySeries(shape: OciShapeSummary): Series {
  const description = (shape.processorDescription ?? '').toLowerCase();
  if (description.includes('amd')) return 'AMD';
  if (description.includes('intel')) return 'Intel';
  return 'Other';
}

function exceedsStandardCaps(
  value: Pick<OciShapeConfig, 'ocpus' | 'memoryInGBs'>,
) {
  return value.ocpus > MAX_OCPUS || value.memoryInGBs > MAX_MEMORY_GB;
}

function ocpuHelperText(blocked: boolean, tooLow: boolean, min: number) {
  if (blocked)
    return `Max ${MAX_OCPUS} — use "Want more?" to request an increase`;
  if (tooLow) return `Min ${min} OCPUs`;
  return `${min}-${MAX_OCPUS} OCPUs`;
}

function memoryHelperText(blocked: boolean, tooLow: boolean, min: number) {
  if (blocked)
    return `Max ${MAX_MEMORY_GB} GB — use "Want more?" to request an increase`;
  if (tooLow) return `Min ${min} GB`;
  return `${min}-${MAX_MEMORY_GB} GB`;
}

function OverageDialog({
  open,
  onClose,
  value,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  value: OciShapeConfig;
  onConfirm: (patch: {
    ocpus: number;
    memoryInGBs: number;
    assignedApprover: string;
    overageReason: string;
  }) => void;
}) {
  const [ocpus, setOcpus] = useState(value.ocpus);
  const [memoryInGBs, setMemoryInGBs] = useState(value.memoryInGBs);
  const [reason, setReason] = useState(value.overageReason ?? '');
  const [approver, setApprover] = useState(value.assignedApprover ?? '');

  useEffect(() => {
    if (open) {
      setOcpus(Math.max(value.ocpus, MAX_OCPUS + 1));
      setMemoryInGBs(Math.max(value.memoryInGBs, MAX_MEMORY_GB + 1));
      setReason(value.overageReason ?? '');
      setApprover(value.assignedApprover ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ocpuOutOfRange = ocpus < value.ocpuMin || ocpus > value.ocpuMax;
  const memoryOutOfRange =
    memoryInGBs < value.memoryMinGb || memoryInGBs > value.memoryMaxGb;
  const canConfirm =
    !ocpuOutOfRange && !memoryOutOfRange && !!reason.trim() && !!approver;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request more OCPUs / Memory</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Requests above {MAX_OCPUS} OCPUs or {MAX_MEMORY_GB} GB memory need
          sign-off from an approver.
        </Typography>
        <Box display="flex" style={{ gap: 24 }} mb={2}>
          <TextField
            label="OCPUs"
            type="number"
            value={ocpus}
            error={ocpuOutOfRange}
            helperText={`${value.ocpuMin}-${value.ocpuMax} OCPUs`}
            onChange={e => setOcpus(Number(e.target.value))}
          />
          <TextField
            label="Memory (GB)"
            type="number"
            value={memoryInGBs}
            error={memoryOutOfRange}
            helperText={`${value.memoryMinGb}-${value.memoryMaxGb} GB`}
            onChange={e => setMemoryInGBs(Number(e.target.value))}
          />
        </Box>
        <TextField
          label="Reason"
          placeholder="Why do you need more than the standard limit?"
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        <OciApproverPickerComponent
          formData={approver}
          onChange={(v: string) => setApprover(v)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canConfirm}
          onClick={() =>
            onConfirm({
              ocpus,
              memoryInGBs,
              assignedApprover: approver,
              overageReason: reason,
            })
          }
        >
          Submit for Approval
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function OciShapePickerComponent({
  onChange,
  formData,
  formContext,
  rawErrors,
}: any) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const theme = useTheme();
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const imageId: string = fd.image?.id ?? '';
  const [shapes, setShapes] = useState<OciShapeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [series, setSeries] = useState<Series>('AMD');
  const [overageOpen, setOverageOpen] = useState(false);

  const value: OciShapeConfig | undefined = formData;

  // Clear a previously-selected shape when the image changes underneath it —
  // that shape may not be compatible with the newly-picked image, and OCI
  // only enforces that at launch time, so we re-force a (now-filtered) pick.
  const prevImageIdRef = useRef(imageId);
  useEffect(() => {
    if (prevImageIdRef.current !== imageId) {
      if (prevImageIdRef.current) {
        onChange(undefined);
      }
      prevImageIdRef.current = imageId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageId]);

  useEffect(() => {
    if (!region || !compartmentId || !imageId) {
      setShapes([]);
      return;
    }
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(
          `${baseUrl}/oci/shapes?region=${encodeURIComponent(
            region,
          )}&compartmentId=${encodeURIComponent(
            compartmentId,
          )}&imageId=${encodeURIComponent(imageId)}`,
        )
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ?? 'Failed to load shapes',
            );
          } else {
            // Flexible shapes only — fixed shapes don't expose ocpu/memory
            // ranges to size against. OCI has already filtered this list to
            // shapes compatible with the selected image.
            setShapes(
              (data.shapes ?? []).filter(
                (s: OciShapeSummary) =>
                  s.ocpuMin !== undefined && s.ocpuMax !== undefined,
              ),
            );
          }
        })
        .catch(() => setError('Failed to load shapes'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId, imageId]);

  const shapesInSeries = useMemo(
    () => shapes.filter(s => classifySeries(s) === series),
    [shapes, series],
  );

  if (!region || !compartmentId) {
    return (
      <FormHelperText>Select a region and compartment first</FormHelperText>
    );
  }

  if (!imageId) {
    return <FormHelperText>Select an image first</FormHelperText>;
  }

  if (loading) return <CircularProgress size={20} />;

  if (error) return <FormHelperText error>{error}</FormHelperText>;

  const selectShape = (shape: OciShapeSummary) => {
    const ocpuMin = shape.ocpuMin ?? 1;
    const memoryMinGb = shape.memoryMinGb ?? 1;
    onChange({
      shape: shape.name,
      processorDescription: shape.processorDescription,
      ocpuMin,
      ocpuMax: shape.ocpuMax ?? ocpuMin,
      memoryMinGb,
      memoryMaxGb: shape.memoryMaxGb ?? memoryMinGb,
      ocpus: Math.min(ocpuMin, MAX_OCPUS),
      memoryInGBs: Math.min(memoryMinGb, MAX_MEMORY_GB),
    });
  };

  const updateSizing = (patch: Partial<OciShapeConfig>) => {
    if (!value) return;
    const next = { ...value, ...patch };
    // Editing the plain fields back within the standard caps clears any
    // previously-recorded approval — it's no longer needed.
    if (!exceedsStandardCaps(next)) {
      next.assignedApprover = undefined;
      next.overageReason = undefined;
    }
    onChange(next);
  };

  // Used by the overage dialog, where the entered values are already
  // validated against the shape's real bounds and don't need re-clamping.
  // Still only keep the approver/reason if the confirmed values actually
  // exceed the standard caps — confirming the dialog with in-range values
  // shouldn't flag the request as needing approval.
  const applyOverage = (patch: Partial<OciShapeConfig>) => {
    if (!value) return;
    const next = { ...value, ...patch };
    if (!exceedsStandardCaps(next)) {
      next.assignedApprover = undefined;
      next.overageReason = undefined;
    }
    onChange(next);
  };

  const ocpuTooLow = value ? value.ocpus < value.ocpuMin : false;
  const memoryTooLow = value ? value.memoryInGBs < value.memoryMinGb : false;
  const needsApproval = value ? exceedsStandardCaps(value) : false;
  const hasApproval = !!(value?.assignedApprover && value?.overageReason);
  const ocpuBlocked = needsApproval && !hasApproval && value!.ocpus > MAX_OCPUS;
  const memoryBlocked =
    needsApproval && !hasApproval && value!.memoryInGBs > MAX_MEMORY_GB;

  return (
    <Box>
      <ButtonGroup
        size="small"
        style={{
          marginBottom: 16,
          borderRadius: 8,
          background: theme.palette.background.default,
          padding: 3,
        }}
      >
        {(['AMD', 'Intel', 'Other'] as Series[]).map(option => (
          <Button
            key={option}
            variant={series === option ? 'contained' : 'text'}
            color={series === option ? 'primary' : 'default'}
            disableElevation
            style={{
              borderRadius: 6,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 64,
            }}
            onClick={() => setSeries(option)}
          >
            {option}
          </Button>
        ))}
      </ButtonGroup>

      {shapesInSeries.length === 0 ? (
        <FormHelperText>
          No {series} shapes are compatible with the selected image. Try another
          series, or go back and pick a different image.
        </FormHelperText>
      ) : (
        <Grid container spacing={2}>
          {shapesInSeries.map(shape => {
            const selected = value?.shape === shape.name;
            return (
              <Grid item xs={12} sm={6} md={4} key={shape.name}>
                <Card
                  variant="outlined"
                  style={{
                    borderColor: selected
                      ? theme.palette.primary.main
                      : undefined,
                    borderWidth: selected ? 2 : 1,
                    borderRadius: 10,
                    transition:
                      'border-color 150ms ease, background-color 150ms ease',
                    background: selected
                      ? `${theme.palette.primary.main}0f`
                      : undefined,
                  }}
                >
                  <CardActionArea
                    component="div"
                    onClick={() => selectShape(shape)}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 10 }}
                      >
                        <Box
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            flex: '0 0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: selected
                              ? `${theme.palette.primary.main}2e`
                              : theme.palette.background.default,
                            color: selected
                              ? theme.palette.primary.main
                              : 'inherit',
                          }}
                        >
                          <MemoryIcon fontSize="small" />
                        </Box>
                        <Typography variant="subtitle1">
                          <strong>{shape.name}</strong>
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: 6 }}
                      >
                        {shape.processorDescription}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {shape.ocpuMin}-{shape.ocpuMax} OCPUs &middot;{' '}
                        {shape.memoryMinGb}-{shape.memoryMaxGb} GB RAM
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {value && (
        <>
          <Box
            mt={3}
            display="flex"
            alignItems="flex-start"
            flexWrap="wrap"
            style={{ gap: 16 }}
          >
            <TextField
              label="OCPUs"
              type="number"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={value.ocpus}
              error={ocpuTooLow || ocpuBlocked}
              helperText={ocpuHelperText(
                ocpuBlocked,
                ocpuTooLow,
                value.ocpuMin,
              )}
              onChange={e => updateSizing({ ocpus: Number(e.target.value) })}
            />
            <TextField
              label="Memory (GB)"
              type="number"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={value.memoryInGBs}
              error={memoryTooLow || memoryBlocked}
              helperText={memoryHelperText(
                memoryBlocked,
                memoryTooLow,
                value.memoryMinGb,
              )}
              onChange={e =>
                updateSizing({ memoryInGBs: Number(e.target.value) })
              }
            />
            <Box
              flex={1}
              minWidth={220}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{
                gap: 12,
                padding: '10px 16px',
                borderRadius: 10,
                border: '1px solid rgba(76,175,154,0.25)',
                background: 'rgba(76,175,154,0.06)',
              }}
            >
              <Box>
                <Typography variant="body2">
                  <strong>Need more resources?</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  You can request a limit increase
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disableElevation
                style={{ textTransform: 'none', flexShrink: 0 }}
                onClick={() => setOverageOpen(true)}
              >
                Request Increase
              </Button>
            </Box>
          </Box>

          {hasApproval && (
            <FormHelperText style={{ marginTop: 8 }}>
              Requires approval from <strong>{value.assignedApprover}</strong>:
              "{value.overageReason}"
            </FormHelperText>
          )}

          <OverageDialog
            open={overageOpen}
            onClose={() => setOverageOpen(false)}
            value={value}
            onConfirm={patch => {
              applyOverage(patch);
              setOverageOpen(false);
            }}
          />
        </>
      )}
      {rawErrors?.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
    </Box>
  );
}

export const OciShapePickerField = createFormField({
  name: 'OciShapePicker',
  component: OciShapePickerComponent,
  validation: (data: OciShapeConfig, field) => {
    if (!data?.shape) {
      field.addError('Select a shape');
      return;
    }
    const hasApproval = !!(data.assignedApprover && data.overageReason);
    if (data.ocpus > MAX_OCPUS && !hasApproval) {
      field.addError(
        `OCPUs above ${MAX_OCPUS} need an approver — use "Want more?"`,
      );
    }
    if (data.ocpus > data.ocpuMax) {
      field.addError(`OCPUs cannot exceed ${data.ocpuMax} for this shape`);
    }
    if (data.ocpus < data.ocpuMin) {
      field.addError(`OCPUs must be at least ${data.ocpuMin}`);
    }
    if (data.memoryInGBs > MAX_MEMORY_GB && !hasApproval) {
      field.addError(
        `Memory above ${MAX_MEMORY_GB} GB needs an approver — use "Want more?"`,
      );
    }
    if (data.memoryInGBs > data.memoryMaxGb) {
      field.addError(
        `Memory cannot exceed ${data.memoryMaxGb} GB for this shape`,
      );
    }
    if (data.memoryInGBs < data.memoryMinGb) {
      field.addError(`Memory must be at least ${data.memoryMinGb} GB`);
    }
  },
});
